<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.monthlyReport.title') }}
        </h4>
        <p class="ml-5 pb-1 mb-1 align-self-end operation-office-initial-announce"
          v-show="operationOfficeInitialMessage && beforeSearchParams == null">
          {{ i18n.global.t('pages.monthlyReport.messages.displayCurrentMonth') }}
        </p>
      </div>
      <div>
        <MonthlyDriverOrVehicleList
          class="mb-2"
          :pageInfo="pageInfo"
          :searched="searched"
          :notReady="notReady"
          :isManagementOffice="isManagementOffice"
          :isOperationOffice="isOperationOffice"
          :isDepartment="isDepartment"
          :items="driverOrVehicleList"
          :pagenate="searchMonthlyReportList"
          :listLoading="listLoading"
          :editableWorkingTime="!!Number(parseSettingsMonthlyReport()['enable_set_start_and_end_of_working_time'])"
          :operationOfficeId="targetOperationOffice?.id"
          :monthly="getMonthly"
          :showFlash="showFlash"
          @select="handleSelectDriverOrVehicleIds"
          @cleared="handleCleared"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          :showItemCount="true"
        />
        <hr />
      </div>
      <h4>
        {{ i18n.global.t('pages.monthlyReport.outputConditions') }}
      </h4>
      <div>
        <MonthlyReportOutputLayout
          @change="setOutputSettings"
          v-model:existDriverOrVehicleList="existDriverOrVehicleList"
          v-model:aggregation="aggregation"
        />
        <div v-show="showableButtonArea">
          <ButtonArea
            :operationOfficeId="operationOfficeId"
            :operationOfficeName="operationOfficeName"
            :groupName="groupName"
            :monthly="monthly"
            :driverOrVehicleIds="selectedDriverOrVehicleIds"
            :outputSettings="selectOutputSettings"
            :showFlash="showFlash"
          />
        </div>
        <MonthlyReportPreCreate
          v-if="isManagementOffice"
          :operationOfficeId="targetOperationOffice?.id"
          :yearMonth="getMonthly"
          :showFlash="showFlash"
        />
      </div>
    </div>
    <MonthlyReportSearchForm
      :loading="listLoading"
      :isOperationOffice="isOperationOffice"
      :isManagementOffice="isManagementOffice"
      :isSubManagementOffice="isSubManagementOffice"
      :isDepartment="isDepartment"
      @formchange="handleChangeSerarchForm"
      @search="searchMonthlyReportList"
    />
  </div>
</template>

<script>
  import { MANAGEMENT_OFFICE_TYPE, SUB_MANAGEMENT_OFFICE_TYPE, OPERATION_OFFICE_TYPE, DEPARTMENT_OFFICE_TYPE } from "@/services/consts.ts"
  import MonthlyReportSearchForm from "@/components/MonthyReportParts/MonthlyReportSearchForm"
  import MonthlyDriverOrVehicleList from "@/components/MonthyReportParts/MonthlyDriverOrVehicleList"
  import MonthlyReportOutputLayout from "@/components/MonthyReportParts/MonthlyReportOutputLayout"
  import MonthlyReportPreCreate from "@/components/MonthyReportParts/MonthlyReportPreCreate"
  import ButtonArea from "@/components/MonthyReportParts/MonthlyReportOutputButtonArea"
  import Api from "@/services/api/ApiServiceFabrick";
  import PaginateWithCount from "@/components/PaginateWithCount.vue"
  import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
  import { getSelfOffice } from "@/services/functions/operationOffice";
  import {ref} from 'vue'
  import { useAuth } from "@/services/user/Auth0UserProvider"
  import i18n from '@/lang/i18n';

  const DRIVER = "driver";
  const VEHICLE = "vehicle";

  export default {
    props: {
      showFlash: {
        type: Function
      },
      switchContentLoading: {
        type: Function
      }
    },
    data() {
      return {
        form: {},
        currentPage: 1,
        totalPage: 0,
        itemsPerPage: 50,
        allElementsCount: 0,
        selectedDriverOrVehicleIds: {},
        listLoading: false,
        searched: false,
        beforeSearchParams: null,
        aggregations: {
          [i18n.global.t('pages.monthlyReport.driver')]: DRIVER,
          [i18n.global.t('pages.monthlyReport.vehicle')]: VEHICLE
        },
        existDriverOrVehicleList: false,
        aggregation: 'driver',
        operationOfficeInitialMessage: false,
        notReady: true,
        targetOperationOffice: null,
        isOperationOffice: false,
        isSubManagementOffice: false,
        isManagementOffice: false,
        isdepartment: false,
        defaultAggregation: i18n.global.t('pages.monthlyReport.driver'),
      }
    },
    computed: {
      borderWidth() {
        return '1000';
      },
      getMonthly() {
        const monthIndex = this.form?.selectYearMonth?.month ?? new Date().getMonth();
        const month = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'][monthIndex]

        const year = this.form?.selectYearMonth?.year ?? new Date().getFullYear();
        return year + '/' + month;
      }
    },
    setup() {
      document.title = i18n.global.t('pages.monthlyReport.title');
      const showableButtonArea = ref(true)
      const setShowableButtonArea = async () => {
        const authorityList = await AuthorityCacheApi.getWithParse()
        showableButtonArea.value = authorityList["octlink.monthly_report.output"]
      }
      setShowableButtonArea()

      const { loginWithRedirect } = useAuth();

      return {
        showableButtonArea,
        loginWithRedirect,
        i18n
      }
    },
    mounted() {
      this.switchContentLoading(true)
      Api().getSelfAccount().then((data) => {
        this.isManagementOffice = data.login_organization_type == MANAGEMENT_OFFICE_TYPE
        this.isSubManagementOffice = data.login_organization_type == SUB_MANAGEMENT_OFFICE_TYPE
        this.isOperationOffice = data.login_organization_type == OPERATION_OFFICE_TYPE
        this.isDepartment = data.login_organization_type == DEPARTMENT_OFFICE_TYPE
        this.switchContentLoading(false)
        this.notReady = false
        if (this.isOperationOffice || this.isDepartment) return this.getInitialMonthlyReport()
      })
    },
    watch: {
      currentPage: {
        handler() {
          this.getMonthlyReports(this.currentPage, false)
        }
      }
    },
    methods: {
      async getInitialMonthlyReport() {
        this.listLoading = true
        let selfDepartment
        let selfOperationOffice
        if (this.isDepartment) {
          selfDepartment = await this.getDepartment()
        }
        selfOperationOffice = await getSelfOffice()
        const request = this.makeInitialParams(selfOperationOffice, selfDepartment)
        this.apiGetMonthlyReports(request, this.defaultAggregation)
      },
      async getDepartment() {
        let department
        await Api().autoCompleteDepartments().then((data) => {
          department = data
        })
        return department
      },
      makeInitialParams(operationoffice, departments = []) {
        const department = departments.length == 1 ? departments[0] : null

        return {
          page: 1,
          operation_office_id: operationoffice?.id,
          management_office_id: operationoffice?.management_officeable_id,
          management_officeable_type: operationoffice?.management_officeable_type,
          department_id: department?.id,
          monthly: this.getMonthly,
          type: this.aggregations[this.defaultAggregation]
        }
      },
      handleChangeSerarchForm(form) {
        this.form = form
      },
      setOutputSettings(settings) {
        this.selectOutputSettings = settings
      },
      handleSelectDriverOrVehicleIds(selectedDriverOrVehicleIds) {
        this.selectedDriverOrVehicleIds = selectedDriverOrVehicleIds
      },
      handleCleared() {
        // 月報がクリアされたら、リストを再読み込み
        this.getMonthlyReports(this.currentPage, false)
      },
      searchMonthlyReportList() {
        this.operationOfficeInitialMessage = false
        this.searched = true
        this.getMonthlyReports()
      },
      getMonthlyReports(page = 1, updateOption = true) {
        this.listLoading = true
        let req
        const aggregation = this.form?.targetOfAggregation
        if (updateOption || !this.beforeSearchParams) {
          req = this.makeSearchParams(page, aggregation)
          this.beforeSearchParams = req
        } else {
          req = this.beforeSearchParams
          req.page = page
        }
        this.apiGetMonthlyReports(req, aggregation)
      },
      apiGetMonthlyReports(req, aggregation) {
        Api().getMonthlyReports(req).then((data) => {
          this.targetOperationOffice = this.form.operationOffice;

          this.driverOrVehicleList = data.items
          this.aggregation = this.aggregations[aggregation]
          this.existDriverOrVehicleList = this.driverOrVehicleList.length != 0
          this.currentPage = data.current_page
          this.totalPage = data.total_page
          this.allElementsCount = data.total
          this.itemsPerPage = data.per_page
          this.operationOfficeInitialMessage = true
          this.listLoading = false

          this.operationOfficeId = this.form.operationOffice?.id
          this.operationOfficeName = this.form.operationOffice?.name
          this.groupName = this.form.group?.name
          this.monthly = this.getMonthly
        }).catch((error) => {
          const res = error.response
          switch (res.status) {
            case 401:
              this.loginWithRedirect({ appState: { targetUrl: location.pathname } })
              break
            case 403:
              location.reload()
              break
            case 422:
          }
          this.listLoading = false
        })
      },
      makeSearchParams(page, aggregation){
        const monthly = this.getMonthly
        const ret = {
          page: page,
          operation_office_id: this.form?.operationOffice?.id,
          management_office_id: this.form?.managementOffice?.id,
          management_officeable_type: this.form?.managementOffice?.type,
          monthly: monthly,
          group_id: this.form?.group?.id,
          department_id: this.form?.department?.id,
          type: this.aggregations[aggregation]
        }
        if (aggregation == i18n.global.t('pages.monthlyReport.driver')) {
          ret['driver_id'] = this.form?.driver?.driver_id
          ret['account_id'] = this.form?.driver?.id
        }
        if (aggregation == i18n.global.t('pages.monthlyReport.vehicle')) ret['vehicle_id'] = this.form?.vehicle?.id

        return ret
      },
      parseSettingsMonthlyReport() {
        if (!this.targetOperationOffice) return {};

        return JSON.parse(this.targetOperationOffice.settings_monthly_report);
      }
    },
    components: {
      MonthlyReportSearchForm,
      MonthlyDriverOrVehicleList,
      MonthlyReportOutputLayout,
      ButtonArea,
      PaginateWithCount,
      MonthlyReportPreCreate,
    }
  }
</script>

<style scoped>
.content {
  min-width: 58vw;
}
</style>
<style>
.main-panel {
  height: initial !important;
  max-height: initial !important;
}
.exec-btn {
  background-color: rgb(21, 130, 156);
  color: white !important;
}
.reset-btn {
  background-color: rgb(245, 246, 247);
  color: rgb(21, 130, 156) !important;
  font-weight: bold;
}
.operation-office-initial-announce {
  font-size: 10px
}
</style>
